<template>
    <section
        :data-mui-theme="theme"
        class="slice slice-content-columns slice-content-columns--duo"
        aria-label="content-columns"
        data-testid="slice-content-columns-duo"
    >
        <div class="slice-content">
            <PrismicRichText
                v-if="isFilled.richText(slice.primary.title)"
                :field="slice.primary.title"
                class="slice-title mui-title"
                data-testid="slice-content-columns-duo-section-title"
            />
            <PrismicRichText
                v-if="isFilled.richText(slice.primary.description)"
                :field="slice.primary.description"
                class="slice-description"
                data-testid="slice-content-columns-duo-section-description"
            />

            <div class="slice-content-columns--duo__body slice-body">
                <ContentColumnsDuoColumn
                    v-for="(column, index) in columns"
                    :key="`duo-column--${index}`"
                    :tag="column.tag"
                    :title="column.title"
                    :description="column.description"
                    :theme="column.theme"
                    :images="column.images"
                    :highlights="column.highlights"
                    :index="index"
                    :align="index === 1 ? 'end' : 'start'"
                />

                <div class="slice-content-columns--duo__connector" aria-hidden="true">
                    <span class="slice-content-columns--duo__connector-half" :data-mui-theme="columns[0]?.theme" />
                    <span class="slice-content-columns--duo__connector-half" :data-mui-theme="columns[1]?.theme" />
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import type {ImageField, KeyTextField, RichTextField} from '@prismicio/types';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {ContentColumnsSlice} from '@prismicio-types';
    import ContentColumnsDuoColumn from '../components/ContentColumnsDuoColumn.vue';

    type ContentColumnsDuoInZone = Extract<ContentColumnsSlice, {variation: 'contentColumnsDuo'}>;
    type DuoColumnTheme = 'default' | 'primary' | 'secondary';

    type DuoColumn = {
        tag: KeyTextField;
        title: RichTextField;
        description: RichTextField;
        theme: DuoColumnTheme;
        images: Array<{image: ImageField}>;
        highlights: RichTextField[];
    };

    const props = defineProps(getSliceComponentProps<ContentColumnsDuoInZone>());

    const theme = computed(() => props.slice.primary.theme);

    const columns = computed((): DuoColumn[] => {
        const {primary} = props.slice;

        return [
            {
                tag: primary.column1Tag,
                title: primary.column1Title,
                description: primary.column1Description,
                theme: primary.column1theme,
                images: primary.column1Images ?? [],
                highlights: primary.column1Highlights.map((item) => item.description) ?? [],
            },
            {
                tag: primary.column2Tag,
                title: primary.column2Title,
                description: primary.column2Description,
                theme: primary.column2theme,
                images: primary.column2Images ?? [],
                highlights: primary.column2Highlights.map((item) => item.description) ?? [],
            },
        ];
    });
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-content-columns--duo {
        // Column halos bleed past the content box; clip them without creating a scroll container
        overflow-x: clip;

        .slice-content {
            text-align: left;
        }

        .slice-description {
            margin: var(--joy-core-spacing-4) 0 0;
            max-width: 640px;
            font-size: var(--joy-font-size-primary-400);
        }

        &__body {
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: var(--joy-core-spacing-12);
            margin-top: var(--joy-core-spacing-10);

            @include mq.screen_md(min) {
                flex-direction: row;
                justify-content: space-between;
                margin-top: var(--joy-core-spacing-12);
            }

            > .slice-content-columns-duo-column {
                position: relative;
                z-index: 1;
            }
        }

        // Sits in the gutter between the two 500px-max columns, around the middle of the image stages
        &__connector {
            display: none;

            @include mq.screen_md(min) {
                display: flex;
                gap: var(--joy-core-spacing-2);
                position: absolute;
                top: 62%;
                left: 50%;
                width: clamp(var(--joy-core-spacing-8), calc(100% - 1000px - 2 * var(--joy-core-spacing-8)), 240px);
                transform: translate(-50%, -50%);
                pointer-events: none;
            }
        }

        &__connector-half {
            flex: 1;
            height: 0;
            border-top: 5px dotted var(--mui-color-shape);
        }
    }
</style>
